import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl, getAuthToken } from '../../../services/api';

const API_BASE = () => `${getApiBaseUrl()}/api/admin/resources`;

const LEVELS = [{ value: 'primary', label: 'Primary' }, { value: 'middle', label: 'Middle' }, { value: 'high', label: 'High' }];

const ExamManagementTab = () => {
  const { t } = useTranslation('resourcesManagement');
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: '', subject: '', description: '', level: 'primary', stream: '', year: '', date: '',
    pdf_path: '', word_path: '', correction_pdf_path: '', correction_word_path: '', has_correction: false,
  });

  const fetchExams = () => {
    const token = getAuthToken();
    const headers = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${API_BASE()}/exams`, { headers })
      .then((r) => r.json())
      .then((data) => data.success && setExams(data.exams || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchExams(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const token = getAuthToken();
    if (!token) return;
    const url = editingId ? `${API_BASE()}/exams/${editingId}` : `${API_BASE()}/exams`;
    const method = editingId ? 'PUT' : 'POST';
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) { setShowForm(false); setEditingId(null); setForm({ title: '', subject: '', description: '', level: 'primary', stream: '', year: '', date: '', pdf_path: '', word_path: '', correction_pdf_path: '', correction_word_path: '', has_correction: false }); fetchExams(); }
        else setError(data.message || 'Failed');
      })
      .catch((e) => setError(e.message));
  };

  const handleDelete = (id, title) => {
    if (!window.confirm(t('examManagement.confirmDelete'))) return;
    const token = getAuthToken();
    if (!token) return;
    fetch(`${API_BASE()}/exams/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => data.success && fetchExams())
      .catch((e) => setError(e.message));
  };

  const startEdit = (exam) => {
    setEditingId(exam.id);
    setForm({
      title: exam.title || '', subject: exam.subject || '', description: exam.description || '', level: exam.level || 'primary', stream: exam.stream || '', year: exam.year || '', date: exam.date || '',
      pdf_path: exam.pdf_path || '', word_path: exam.word_path || '', correction_pdf_path: exam.correction_pdf_path || '', correction_word_path: exam.correction_word_path || '', has_correction: !!exam.has_correction,
    });
    setShowForm(true);
  };

  if (loading) return <div className="loading-message">Loading exams...</div>;
  return (
    <div className="tab-content">
      {error && <div className="error-message">{error}</div>}
      <div style={{ marginBottom: 16 }}>
        <button type="button" className="action-btn" onClick={() => { setEditingId(null); setForm({ title: '', subject: '', description: '', level: 'primary', stream: '', year: '', date: '', pdf_path: '', word_path: '', correction_pdf_path: '', correction_word_path: '', has_correction: false }); setShowForm(!showForm); }}>
          {showForm ? t('examManagement.cancel') : t('examManagement.addExam')}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ marginBottom: 24, padding: 16, border: '1px solid var(--border)', borderRadius: 8 }}>
          <h4>{editingId ? t('examManagement.editExam') : t('examManagement.addExam')}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, maxWidth: 600 }}>
            <label>Title * <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></label>
            <label>Subject * <input type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required /></label>
            <label>Level *
              <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
                {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </label>
            <label>Year * <input type="text" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="e.g. Year 1, BEM" required /></label>
            <label>PDF path <input type="text" value={form.pdf_path} onChange={(e) => setForm({ ...form, pdf_path: e.target.value })} placeholder="path/after/upload" /></label>
            <label>Word path <input type="text" value={form.word_path} onChange={(e) => setForm({ ...form, word_path: e.target.value })} /></label>
          </div>
          <button type="submit" className="action-btn" style={{ marginTop: 12 }}>Save</button>
        </form>
      )}

      <div className="resources-table-wrap">
        <table className="resources-table">
          <thead>
            <tr>
              <th>{t('examManagement.title')}</th>
              <th>{t('examManagement.level')}</th>
              <th>{t('examManagement.year')}</th>
              <th>{t('examManagement.subject')}</th>
              <th>{t('examManagement.fileTypes')}</th>
              <th>{t('examManagement.downloads')}</th>
              <th>{t('examManagement.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {exams.map((row) => (
              <tr key={row.id}>
                <td>{row.title}</td>
                <td>{row.level}</td>
                <td>{row.year}</td>
                <td>{row.subject}</td>
                <td>{[row.pdf_path && 'PDF', row.word_path && 'Word'].filter(Boolean).join(', ') || '-'}</td>
                <td>{row.download_count ?? 0}</td>
                <td>
                  <button type="button" className="action-btn" onClick={() => startEdit(row)}>Edit</button>
                  <button type="button" className="action-btn danger" onClick={() => handleDelete(row.id, row.title)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!exams.length && <p style={{ padding: 16 }}>{t('dashboard.noExams')}</p>}
      </div>
    </div>
  );
};

export default ExamManagementTab;
